import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { startOfWeek, addDays, addWeeks, subWeeks, format, parse, isSameDay, addMinutes, differenceInMinutes, addHours, isValid } from "date-fns";
import { HiPlusCircle, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import Draggable from "react-draggable";
import { XCircle, Users, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react"; 

export default function TimeTable() {
  const [activeTab, setActiveTab] = useState("live");
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isVideoLessonModalOpen, setIsVideoLessonModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    capacity: 8,
    timeSlots: {
      Monday: { opening: "", closing: "" },
      Tuesday: { opening: "", closing: "" },
      Wednesday: { opening: "", closing: "" },
      Thursday: { opening: "", closing: "" },
      Friday: { opening: "", closing: "" },
      Saturday: { opening: "", closing: "" },
      Sunday: { opening: "", closing: "" },
    },
  });
  const [holidays, setHolidays] = useState([]);
  const [liveLessons, setLiveLessons] = useState([]);
  const [selectedLiveLesson, setSelectedLiveLesson] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [videoLessons, setVideoLessons] = useState([]);
  const [lessonCache, setLessonCache] = useState({});
  const [selectedVideoLessons, setSelectedVideoLessons] = useState([]);
  const [isHoldTimeSlotModalOpen, setIsHoldTimeSlotModalOpen] = useState(false);
  const [holdFormData, setHoldFormData] = useState({ selectedDate: "", selectedTimeSlot: "", remark: "" });
  const [heldTimeSlots, setHeldTimeSlots] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [definedSlots, setDefinedSlots] = useState([]);
  const [isFetchingTimeSlots, setIsFetchingTimeSlots] = useState(false);
  const [timeSlotRecords, setTimeSlotRecords] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedLiveLessonId, setSelectedLiveLessonId] = useState(null);
  const [attendanceResults, setAttendanceResults] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [students, setStudents] = useState([])
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState("");
  const [sidebarTab, setSidebarTab] = useState("daily");
  const [todaysVideoLessons, setTodaysVideoLessons] = useState([]);
  const gridRef = useRef(null);
  const [isCurrentQRModalOpen, setIsCurrentQRModalOpen] = useState(false);
  const [currentLessonInfo, setCurrentLessonInfo] = useState(null);
  const [isAddIpadModalOpen, setIsAddIpadModalOpen] = useState(false);
  const [ipadFormData, setIpadFormData] = useState({
    startDate: "",
    endDate: "",
    capacity: 1,
  });
  const [isUrgentTimeSlotModalOpen, setIsUrgentTimeSlotModalOpen] = useState(false);
  const [urgentFormData, setUrgentFormData] = useState({
    date: "",
    start_time: "",
    end_time: "",
    ipad_capacity: 1,
  });

  //helper date string
  const toDateStr = (date) => {
    return format(date, "yyyy-MM-dd");
  };

  // Generate week dates (Monday to Sunday)
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(currentWeek, i);
    return {
      day: format(date, "EEEE"),
      date: format(date, "MMM d"),
      fullDate: date,
    };
  });

  // Generate time slots from 9:00 AM to 9:00 PM (30-minute intervals)
  const timeSlots = Array.from({ length: 25 }, (_, i) => {
    const hour = 9 + Math.floor(i / 2);
    const minute = (i % 2) * 30;
    const startTime = `${hour}:${minute.toString().padStart(2, "0")}`;
    const endHour = hour + (minute === 30 ? 1 : 0);
    const endMinute = minute === 30 ? 0 : 30;
    const endTime = `${endHour}:${endMinute.toString().padStart(2, "0")}`;
    return `${startTime} - ${endTime}`;
  });

  // helper for color class
  const getSlotColorClass = (color) => {
    switch (color) {
      case "green":  return "bg-green-200 border-green-400";
      case "blue":   return "bg-blue-200 border-blue-400";
      case "yellow": return "bg-yellow-200 border-yellow-400";
      case "red":    return "bg-red-200 border-red-400";
      default:       return "bg-blue-200 border-blue-400";
    }
  };

  const getLessonDurationSlots = (lesson) => {
    if (!lesson.start_time || !lesson.end_time) return 1;
    const start = parse(lesson.start_time, "HH:mm:ss", new Date());
    const end = parse(lesson.end_time, "HH:mm:ss", new Date());
    if (!isValid(start) || !isValid(end)) return 1;
    const durationMinutes = differenceInMinutes(end, start);
    return Math.max(1, Math.ceil(durationMinutes / 30));
  };

  // Fetch holidays
  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHoliday`);
      if (response.data.success) {
        // Validate and filter holidays
        const validHolidays = response.data.data
          .filter(h => h.holiday && h.holiday_name && /^\d{4}-\d{2}-\d{2}$/.test(h.holiday))
          .map(h => ({
            holiday_name: h.holiday_name,
            holiday: h.holiday,
          }));
        console.log("Fetched holidays:", validHolidays);
        setHolidays(validHolidays);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      alert("Failed to fetch holidays.");
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  const fetchLiveLessons = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLiveLessons`);
      setLiveLessons(response.data.data);
    } catch (error) {
      console.error("Error fetching live lessons:", error);
    }
  };

  const fetchVideoLessons = async (startDate) => {
    const cacheKey = format(startDate, "yyyy-MM-dd");
    if (lessonCache[cacheKey]) {
      console.log("Using cached video lessons for week:", cacheKey);
      setVideoLessons(lessonCache[cacheKey]);
      return;
    }

    try {
      const start = format(startDate, "yyyy-MM-dd");
      const end = format(addDays(startDate, 6), "yyyy-MM-dd");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoLessonsByDateRange?start=${start}&end=${end}`);
      const lessons = Array.isArray(response.data) ? response.data.map(lesson => ({
        ...lesson,
        date: format(new Date(lesson.date), "yyyy-MM-dd")
      })) : [];
      console.log("Fetched video lessons:", lessons);
      setVideoLessons(lessons);
      setLessonCache((prev) => ({ ...prev, [cacheKey]: lessons }));
    } catch (error) {
      console.error("Error fetching video lessons:", error);
      setVideoLessons([]);
    }
  };

  const fetchTodaysVideoLessons = async () => {
    const todayStr = format(new Date(), "yyyy-MM-dd");
    const cacheKey = `today-${todayStr}`;
    if (lessonCache[cacheKey]) {
      setTodaysVideoLessons(lessonCache[cacheKey]);
      return;
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoLessonsByDateRange?start=${todayStr}&end=${todayStr}`);
      let lessons = Array.isArray(response.data) ? response.data.map(lesson => ({
        ...lesson,
        date: todayStr
      })) : [];
      // Sort by start_time
      lessons = lessons.sort((a, b) => {
        const timeA = parse(a.start_time, "HH:mm:ss", new Date()).getTime();
        const timeB = parse(b.start_time, "HH:mm:ss", new Date()).getTime();
        return timeA - timeB;
      });
      setTodaysVideoLessons(lessons);
      setLessonCache((prev) => ({ ...prev, [cacheKey]: lessons }));
    } catch (error) {
      console.error("Error fetching today's video lessons:", error);
      setTodaysVideoLessons([]);
    }
  };

  const getVideoLessonSlots = (lesson, slot) => {
    const lessonStart = parse(lesson.start_time, "HH:mm:ss", new Date());
    const lessonEnd = parse(lesson.end_time, "HH:mm:ss", new Date());
    const lessonStartMinutes = lessonStart.getHours() * 60 + lessonStart.getMinutes();
    const lessonEndMinutes = lessonEnd.getHours() * 60 + lessonEnd.getMinutes();

    const [slotStartStr] = slot.split(" - ");
    const slotStart = parse(slotStartStr, "HH:mm", new Date());
    const slotStartMinutes = slotStart.getHours() * 60 + slotStart.getMinutes();
    const slotEndMinutes = slotStartMinutes + 30;

    return lessonStartMinutes < slotEndMinutes && lessonEndMinutes > slotStartMinutes;
  };

  const goToPreviousWeekVideo = () => {
    const newWeek = subWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
  };

  const goToNextWeekVideo = () => {
    const newWeek = addWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
  };

  const goToPreviousWeekLive = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const goToNextWeekLive = () => setCurrentWeek(addWeeks(currentWeek, 1));

  useEffect(() => {
    fetchHolidays();
    fetchStudents();
    if (activeTab === "live") {
      fetchLiveLessons();
    } else if (activeTab === "video") {
      fetchVideoLessons(currentWeek);
      fetchTodaysVideoLessons();
      fetchDefinedSlotsWithOccupancy(currentWeek);   // ← added
    }
  }, [activeTab, currentWeek]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [isDragging]);

  const handleFormSubmit = async () => {
    const payload = {
      startDate: formData.startDate,
      endDate: formData.endDate,
      capacity: formData.capacity,
      timeSlots: formData.timeSlots,
    };
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/addTimeSlots`, payload);
      setIsScheduleModalOpen(false);
      alert("Schedule created successfully!");
      fetchLiveLessons();
    } catch (error) {
      console.error("Error submitting schedule:", error);
      alert("Failed to create schedule.");
    }
  };

  const handleLiveCellClick = (day, timeSlot, lesson = null) => {
    if (lesson) {
      setSelectedLiveLesson({ ...lesson, day, timeSlot });
    }
  };

  const handleVideoCellClick = (day, timeSlot, lessons) => {
    setSelectedVideoLessons(lessons.map(lesson => ({ ...lesson, day, timeSlot })));
    setIsVideoLessonModalOpen(true);
  };

  const handleDragStop = async (e, dragData, lesson) => {
    setIsDragging(false);

    const gridRect = gridRef.current?.getBoundingClientRect();
    if (!gridRect) return;

    const relativeX = e.clientX - gridRect.left;
    const relativeY = e.clientY - gridRect.top;

    const headerElement = gridRef.current.children[1];
    const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 50;

    const timeElement = gridRef.current.children[0];
    const timeWidth = timeElement ? timeElement.getBoundingClientRect().width : 150;

    const dayElements = Array.from({ length: 7 }, (_, i) => gridRef.current.children[1 + i]);
    const colWidths = dayElements.map(el => el ? el.getBoundingClientRect().width : 0);
    const totalContentWidth = colWidths.reduce((a, b) => a + b, 0);
    const colWidth = totalContentWidth / 7 || (gridRect.width - timeWidth) / 7;

    if (relativeX < timeWidth || relativeY < headerHeight) return;

    const contentX = relativeX - timeWidth;
    const targetDayIndex = Math.floor(contentX / colWidth);
    if (targetDayIndex < 0 || targetDayIndex > 6) return;

    const contentY = relativeY - headerHeight;
    const rowElement = gridRef.current.children[9]; // First content cell
    const rowHeight = rowElement ? rowElement.getBoundingClientRect().height : 56;

    const targetSlotIndex = Math.floor(contentY / rowHeight);
    if (targetSlotIndex < 0 || targetSlotIndex >= timeSlots.length) return;

    const targetDate = weekDays[targetDayIndex].fullDate;
    if (isHoliday(targetDate)) {
      alert("Cannot drop on holiday.");
      return;
    }

    const newTimeSlot = timeSlots[targetSlotIndex];
    const [newStartTime] = newTimeSlot.split(" - ");

    const originalStart = parse(lesson.start_time, "HH:mm:ss", new Date());
    const originalEnd = parse(lesson.end_time, "HH:mm:ss", new Date());
    const durationMinutes = differenceInMinutes(originalEnd, originalStart);
    const newStartDateTime = parse(newStartTime, "HH:mm", new Date());
    const newEndDateTime = addMinutes(newStartDateTime, durationMinutes);

    const originalDate = parse(lesson.lesson_date, "yyyy-MM-dd", new Date());
    const originalDateFormatted = format(originalDate, "MMM d, yyyy");
    const originalTimeFormatted = `${lesson.start_time.slice(0, 5)}-${lesson.end_time.slice(0, 5)}`;
    const newDateFormatted = format(targetDate, "MMM d, yyyy");
    const newTimeFormatted = `${newStartTime}-${format(newEndDateTime, "HH:mm")}`;

    const payload = {
      class_name: lesson.class_name,
      lesson_date: format(targetDate, "yyyy-MM-dd"),
      original_lesson_date: lesson.lesson_date,
      start_time: format(newStartDateTime, "HH:mm:ss"),
      end_time: format(newEndDateTime, "HH:mm:ss"),
    };

    if (window.confirm(`Update ${lesson.class_name} to ${newTimeFormatted}, ${newDateFormatted} from ${originalTimeFormatted}, ${originalDateFormatted}?`)) {
      try {
        await axios.patch(`${process.env.REACT_APP_API_URL}/admin/updateLesson`, payload);
        setLiveLessons((prev) =>
          prev.map((l) =>
            l.class_id === lesson.class_id && l.lesson_date === lesson.lesson_date && l.start_time === lesson.start_time
              ? { ...l, lesson_date: payload.lesson_date, start_time: payload.start_time, end_time: payload.end_time }
              : l
          )
        );
        setSelectedLiveLesson(null);
        alert("Lesson updated successfully!");
      } catch (error) {
        console.error("Error updating lesson:", error);
        alert("Failed to update lesson.");
      }
    }
  };

  // Check if a day is a holiday
  const isHoliday = (date) => {
    return holidays.some((holiday) => {
      if (!holiday.holiday) {
        console.warn("Invalid holiday date:", holiday);
        return false;
      }
      try {
        const holidayDate = parse(holiday.holiday, "yyyy-MM-dd", new Date());
        return isSameDay(holidayDate, date);
      } catch (error) {
        console.warn("Error parsing holiday date:", holiday, error);
        return false;
      }
    });
  };

  const getLessonSlots = (lesson, day) => {
    if (!lesson.lesson_date || !day.fullDate) {
      console.warn("Invalid lesson or day:", lesson, day);
      return [];
    }

    const lessonDate = parse(lesson.lesson_date, "yyyy-MM-dd", new Date());
    if (!isSameDay(lessonDate, day.fullDate)) return [];

    const lessonStart = parse(lesson.start_time, "HH:mm:ss", new Date());
    const lessonEnd = parse(lesson.end_time, "HH:mm:ss", new Date());
    const lessonStartMinutes = lessonStart.getHours() * 60 + lessonStart.getMinutes();
    const lessonEndMinutes = lessonEnd.getHours() * 60 + lessonEnd.getMinutes();

    return timeSlots
      .map((slot, index) => {
        const [slotStartStr] = slot.split(" - ");
        const slotStart = parse(slotStartStr, "HH:mm", new Date());
        const slotStartMinutes = slotStart.getHours() * 60 + slotStart.getMinutes();

        if (lessonStartMinutes <= slotStartMinutes && lessonEndMinutes > slotStartMinutes) {
          return { slot, index, isFirstSlot: slotStartMinutes === lessonStartMinutes };
        }
        return null;
      })
      .filter(Boolean);
  };

  const fetchAvailableTimeSlots = async (selectedDate) => {
    if (!selectedDate) {
      setAvailableTimeSlots([]);
      return;
    }

    const utcDate = new Date(selectedDate);
    const hktDate = addHours(utcDate, 8);
    const formattedDate = format(hktDate, 'yyyy-MM-dd');

    setIsFetchingTimeSlots(true);
    setFetchError(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getAvailableTimeSlots`, {
        params: { selectedDate: formattedDate },
      });
      const slots = response.data.map(slot => `${slot.start_time.slice(0, 5)} - ${slot.end_time.slice(0, 5)}`);
      setAvailableTimeSlots(slots);
    } catch (error) {
      setFetchError(error.response?.status === 404 ? "No time slots available for this date." : "Failed to fetch time slots.");
      setAvailableTimeSlots([]);
    } finally {
      setIsFetchingTimeSlots(false);
    }
  };

  const fetchDefinedSlotsWithOccupancy = async (startDate) => {
    try {
      const start = toDateStr(startDate);
      const end = toDateStr(addDays(startDate, 6));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/getDefinedTimeSlotsWithOccupancy?start=${start}&end=${end}`
      );
      setDefinedSlots(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching defined time slots with occupancy:", error);
      setDefinedSlots([]);
    }
  };

  const fetchTimeSlotRecords = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getTimeSlotCreateRecords`);
      if (response.data.success) {
        setTimeSlotRecords(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching time slot records:", error);
    }
  };

  useEffect(() => {
    if (holdFormData.selectedDate) {
      fetchAvailableTimeSlots(holdFormData.selectedDate);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [holdFormData.selectedDate]);

  useEffect(() => {
    if (isHoldTimeSlotModalOpen) {
      const fetchHeldSlots = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHeldTimeSlots`, {
            headers: { 'Cache-Control': 'no-cache' }
          });
          setHeldTimeSlots(response.data);
        } catch (error) {
          console.error('Error fetching held time slots:', error);
          setFetchError('Failed to load held time slots.');
        }
      };
      fetchHeldSlots();
    }
  }, [isHoldTimeSlotModalOpen]);

  useEffect(() => {
    if (activeTab === "video") {
      fetchTimeSlotRecords();
    } else {
      setTimeSlotRecords([]);
    }
  }, [activeTab]);

  // Attendance-related functions
  const handleManageLessonAttendance = async (lesson) => {
    try {
      // First, fetch the live_lesson_id using class_id, lesson_date, start_time
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLiveLessonId`, {
        params: {
          class_id: lesson.class_id,
          lesson_date: lesson.lesson_date,
          start_time: lesson.start_time,
        },
      });
      if (response.data.success && response.data.lesson_id) {
        setSelectedLiveLessonId(response.data.lesson_id);
        // Then fetch attendance
        const attendanceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLessonAttendance/${response.data.lesson_id}`);
        if (attendanceResponse.data.success) {
          setAttendanceResults(attendanceResponse.data.data);
        } else {
          setAttendanceResults([]);
        }
        setShowAttendanceModal(true);
      } else {
        alert('Lesson not found. Please ensure the lesson exists.');
      }
    } catch (error) {
      console.error('Error fetching lesson attendance:', error);
      alert('Failed to fetch attendance data.');
    }
  };

  const toggleAttendance = (index) => {
    const newResults = [...attendanceResults];
    const current = newResults[index].attendance;
    newResults[index].attendance =
      current === 'pending' ? 'presence' :
      current === 'presence' ? 'absence' :
      current === 'absence' ? 'leave' :
      'pending';  // Back to pending if 'leave'
    setAttendanceResults(newResults);
  };

  // Toggle homework_status between 0, 1, 2
  const toggleHomeworkStatus = (index) => {
    const newResults = [...attendanceResults];
    const current = newResults[index].homework_status;
    newResults[index].homework_status = current === 0 ? 1 : current === 1 ? 2 : 0;
    setAttendanceResults(newResults);
  };

  const handleOpenQRModal = async (lesson) => {
    try {
      // Fetch live_lesson_id (reuse existing endpoint)
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getLiveLessonId`, {
        params: {
          class_id: lesson.class_id,
          lesson_date: lesson.lesson_date,
          start_time: lesson.start_time,
        },
      });
      if (response.data.success && response.data.lesson_id) {
        // Fetch attendance code
        const qrResponse = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getAttendanceCode/${response.data.lesson_id}`);
        if (qrResponse.data.success) {
          setSelectedQRCode(qrResponse.data.data);
          setIsQRModalOpen(true);
        } else {
          alert('Attendance code not found for this lesson.');
        }
      } else {
        alert('Lesson not found. Please ensure the lesson exists.');
      }
    } catch (error) {
      console.error('Error fetching attendance code for QR:', error);
      alert('Failed to fetch attendance code.');
    }
  };

  const fetchCurrentOrNextLessonQR = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getCurrentOrNextLessonQR`);
      if (response.data.success) {
        setCurrentLessonInfo(response.data.data);
      } else {
        setCurrentLessonInfo(null);
        alert(response.data.message || 'No current or upcoming lesson found.');
      }
    } catch (error) {
      console.error('Error fetching current/next lesson QR:', error);
      setCurrentLessonInfo(null);
      alert('Failed to fetch current/next lesson QR.');
    }
  };

  const handleShowCurrentQR = () => {
    fetchCurrentOrNextLessonQR();
    setIsCurrentQRModalOpen(true);
  };

  const handleAddIpadCapacity = async () => {
  if (!ipadFormData.startDate) {
    alert("Please select a start date.");
    return;
  }

  const payload = {
    startDate: ipadFormData.startDate,
    endDate: ipadFormData.endDate || ipadFormData.startDate, // single day if endDate empty
    capacity: ipadFormData.capacity,
  };

  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/admin/addIpadCapacity`, payload);
    
    alert("iPad capacity added successfully!");
    setIsAddIpadModalOpen(false);
    setIpadFormData({ startDate: "", endDate: "", capacity: 1 });
    
    // Optional: refresh the active schedules list
    fetchTimeSlotRecords();
  } catch (error) {
    console.error("Error adding iPad capacity:", error);
    alert(error.response?.data?.message || "Failed to add iPad capacity.");
  }
  };

  const handleCreateUrgentTimeSlot = async () => {
    const { date, start_time, end_time, ipad_capacity } = urgentFormData;

    if (!date || !start_time || !end_time || !ipad_capacity) {
      alert("Please fill in all fields.");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/createUrgentTimeSlot`, {
        date,
        start_time: start_time.length === 5 ? `${start_time}:00` : start_time, // ensure HH:mm:ss
        end_time: end_time.length === 5 ? `${end_time}:00` : end_time,
        ipad_capacity: Number(ipad_capacity),
      });

      alert("Urgent time slot created successfully!");
      setIsUrgentTimeSlotModalOpen(false);
      setUrgentFormData({ date: "", start_time: "", end_time: "", ipad_capacity: 1 });

      // Refresh the calendar data
      fetchDefinedSlotsWithOccupancy(currentWeek);
      fetchVideoLessons(currentWeek);
    } catch (error) {
      console.error("Error creating urgent time slot:", error);
      alert(error.response?.data?.message || "Failed to create urgent time slot.");
    }
  };

  useEffect(() => {
    let interval;
    if (isCurrentQRModalOpen) {
      interval = setInterval(() => {
        fetchCurrentOrNextLessonQR();
      }, 60000); // Refresh every 1 minute
    }
    return () => clearInterval(interval);
  }, [isCurrentQRModalOpen]);

  // Render visual indicator for attendance
  const renderAttendanceIndicator = (attendance) => {
    if (attendance === 'presence') return '✓';
    if (attendance === 'absence') return '✗';
    if (attendance === 'leave') return 'L';
    return '';  // For 'pending'
  };

  // Render visual indicator for homework_status
  const renderHomeworkIndicator = (status) => {
    if (status === 1) return '✓';
    if (status === 2) return '✗';
    return '';
  };

  // Get student name by student_id
  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.surname} ${student.first_name}` : 'Unknown Student';
  };

// Update attendance on submit
  const handleUpdateAttendance = async (e) => {
    e.preventDefault();
    if (attendanceResults.length === 0) {
      alert('No attendance data available.');
      return;
    }
    setLoadingAttendance(true);
    try {
      console.log('Submitting attendance update for lesson ID:', selectedLiveLessonId);  // Debug log
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/updateAttendanceRecord`, {  // Changed endpoint
        live_lesson_id: selectedLiveLessonId,
        results: attendanceResults.map((result) => ({
          student_id: result.student_id,
          attendance: result.attendance || 'pending',
          homework_status: Number(result.homework_status) || 0,
        })),
      });
      console.log('Attendance update successful');  // Debug log
      alert('Attendance updated successfully.');
      setShowAttendanceModal(false);
      setAttendanceResults([]);
      setSelectedLiveLessonId(null);
    } catch (error) {
      console.error('Error updating attendance:', error);
      console.error('Error response:', error.response?.data);  // Enhanced logging
      alert(error.response?.data?.error || 'Failed to update attendance.');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const renderLiveCalendar = () => (
    <div className="w-full mx-auto overflow-x-auto max-w-[1440px] p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={goToPreviousWeekLive}
            className="p-2 rounded-full hover:bg-gray-200 transition"
            aria-label="Previous week"
          >
            <HiChevronLeft className="w-6 h-6" />
          </button>
          <span className="text-lg font-semibold select-none">
            {format(currentWeek, "MMM d")} - {format(addDays(currentWeek, 6), "MMM d, yyyy")}
          </span>
          <button
            onClick={goToNextWeekLive}
            className="p-2 rounded-full hover:bg-gray-200 transition"
            aria-label="Next week"
          >
            <HiChevronRight className="w-6 h-6" />
          </button>
          <input
            type="date"
            value={format(currentWeek, 'yyyy-MM-dd')}
            onChange={(e) => {
              const date = parse(e.target.value, 'yyyy-MM-dd', new Date());
              setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
            }}
            className="p-1 border border-gray-300 rounded text-sm"
          />
        </div>
        <button
          onClick={handleShowCurrentQR}
          className="bg-blue-600 text-white px-5 py-2 rounded flex items-center gap-2 hover:bg-blue-700 transition"
        >
          <QrCode className="w-5 h-5" />
          Show Current Class QRCode
        </button>
      </div>
      <div className="grid grid-cols-[150px_repeat(7,_1fr)] gap-px bg-gray-300 border border-gray-300 min-w-[1000px]" ref={gridRef}>
        <div className="bg-white p-2 font-semibold sticky top-0 z-10"></div>
        {weekDays.map((day, i) => (
          <div
            key={i}
            className={`bg-white p-2 font-semibold text-center sticky top-0 z-10 select-none ${
              isHoliday(day.fullDate) ? "bg-gray-200" : ""
            }`}
          >
            <div>{day.day}</div>
            <div className="text-sm text-gray-500">{day.date}</div>
          </div>
        ))}
        {timeSlots.map((slot, slotIndex) => (
          <React.Fragment key={slotIndex}>
            <div
              className={`bg-white p-2 text-right border-t border-gray-200 select-none ${
                slotIndex % 2 === 1 ? "border-t-transparent" : ""
              }`}
            >
              {slot.split(" - ")[0]}
            </div>
            {weekDays.map((day, dayIndex) => {
              const isHolidayDay = isHoliday(day.fullDate);
              if (isHolidayDay) {
                return (
                  <div
                    key={`${day.date}-${slotIndex}`}
                    className="h-14 border border-gray-200 bg-gray-200 cursor-not-allowed"
                  />
                );
              }

              const overlappingLessons = liveLessons.filter((lesson) => {
                const slots = getLessonSlots(lesson, day);
                return slots.some((s) => s.slot === slot);
              });

              if (overlappingLessons.length === 0) {
                return (
                  <div
                    key={`${day.date}-${slotIndex}`}
                    className="relative h-14 border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleLiveCellClick(day.day, slot)}
                  />
                );
              }

              const lesson = overlappingLessons[0];
              const slots = getLessonSlots(lesson, day);
              const slotInfo = slots.find((s) => s.slot === slot);
              const isFirstSlot = slotInfo?.isFirstSlot;
              const durationSlots = getLessonDurationSlots(lesson);
              const rowHeight = 56;

              if (isFirstSlot) {
                return (
                  <div
                    key={`${day.date}-${slotIndex}`}
                    className="relative h-14 bg-white"
                  >
                    <Draggable
                      bounds="parent"
                      onStart={() => setIsDragging(true)}
                      onStop={(e, data) => handleDragStop(e, data, lesson)}
                    >
                      <div
                        className="absolute inset-0 bg-blue-100 rounded-md border border-blue-200 cursor-move hover:bg-blue-200 transition p-2 relative z-10"
                        style={{
                          height: `${durationSlots * rowHeight}px`,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLiveCellClick(day.day, slot, lesson);
                        }}
                      >
                        <div className="h-full flex flex-col justify-between">
                          <div className="text-sm font-medium leading-tight">
                            {lesson.class_name}
                            <br />
                            <span className="text-xs text-gray-600">
                              {lesson.start_time.slice(0, 5)} - {lesson.end_time.slice(0, 5)}
                            </span>
                          </div>
                          <div className="flex justify-end space-x-1">
                            <button
                              className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageLessonAttendance(lesson);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                            >
                              <Users className="w-4 h-4 text-purple-600" />
                            </button>
                            <button
                              className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenQRModal(lesson);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                            >
                              <QrCode className="w-4 h-4 text-green-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Draggable>
                  </div>
                );
              } else {
                return (
                  <div
                    key={`${day.date}-${slotIndex}`}
                    className="relative z-0 h-14 bg-transparent border-l border-r border-b border-gray-200 border-t-0"
                    onClick={() => handleLiveCellClick(day.day, slot, lesson)}
                  />
                );
              }
            })}
          </React.Fragment>
        ))}
      </div>
      {selectedLiveLesson && (
        <Draggable onStart={() => setIsDragging(true)} onStop={() => setIsDragging(false)}>
          <div className="fixed bg-white border border-gray-300 p-4 rounded shadow-lg z-30 max-w-xs">
            <h3 className="font-bold">{selectedLiveLesson.class_name}</h3>
            <p>Date: {format(parse(selectedLiveLesson.lesson_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}</p>
            <p>Time: {selectedLiveLesson.timeSlot}</p>
            <button
              className="mt-2 bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition"
              onClick={() => setSelectedLiveLesson(null)}
            >
              Close
            </button>
          </div>
        </Draggable>
      )}
      {/* {isPlusModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-md">
            <h2 className="text-xl font-bold mb-4">Additional Options</h2>
            <p>Modal for lesson: {selectedLessonForPlus?.class_name}</p>
            <p>TBD: Content to be specified.</p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setIsPlusModalOpen(false);
                  setSelectedLessonForPlus(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )} */}
      {/* Attendance Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-3/4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Manage Attendance for Lesson ID: {selectedLiveLessonId}</h3>
              <button
                onClick={() => setShowAttendanceModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleUpdateAttendance}>
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-blue-100 sticky top-0">
                      <th className="px-4 py-2 border">Student</th>
                      <th className="px-4 py-2 border">Attendance</th>
                      <th className="px-4 py-2 border">Homework Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceResults.map((result, index) => (
                      <tr key={`${result.student_id}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 border">{`${result.student_id} (${getStudentName(result.student_id)})`}</td>
                        <td className="px-4 py-2 border text-center">
                          <button
                            type="button"
                            onClick={() => toggleAttendance(index)}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {renderAttendanceIndicator(result.attendance)}
                          </button>
                        </td>
                        <td className="px-4 py-2 border text-center">
                          <button
                            type="button"
                            onClick={() => toggleHomeworkStatus(index)}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {renderHomeworkIndicator(result.homework_status)}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAttendanceModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                    loadingAttendance && 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={loadingAttendance}
                >
                  {loadingAttendance ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderVideoCalendar = () => (
    <div className="w-[100%] mx-auto max-w-[1440px]">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goToPreviousWeekVideo}
            className="p-2 rounded-full hover:bg-gray-200"
            aria-label="Previous week"
          >
            <HiChevronLeft className="w-6 h-6" />
          </button>
          <span className="text-lg font-semibold">
            {format(currentWeek, "MMM d")} - {format(addDays(currentWeek, 6), "MMM d, yyyy")}
          </span>
          <button
            onClick={goToNextWeekVideo}
            className="p-2 rounded-full hover:bg-gray-200"
            aria-label="Next week"
          >
            <HiChevronRight className="w-6 h-6" />
          </button>
          <input
            type="date"
            value={format(currentWeek, 'yyyy-MM-dd')}
            onChange={(e) => {
              const date = parse(e.target.value, 'yyyy-MM-dd', new Date());
              setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
            }}
            className="p-1 border border-gray-300 rounded text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setIsScheduleModalOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded flex items-center hover:bg-blue-600"
          >
            <HiPlusCircle className="w-5 h-5" />
            Add Schedule
          </button>
          <button
            onClick={() => setIsHoldTimeSlotModalOpen(true)}
            className="bg-green-500 text-white px-4 py-2 rounded flex items-center hover:bg-green-600"
          >
            <HiPlusCircle className="w-5 h-5" />
            Hold Time Slot
          </button>
          <button
            onClick={() => setIsUrgentTimeSlotModalOpen(true)}
            className="bg-orange-500 text-white px-4 py-2 rounded flex items-center hover:bg-orange-600"
          >
            <HiPlusCircle className="w-5 h-5" />
            Urgent Time Slot
          </button>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-[80px_repeat(7,_1fr)] gap-1 border border-gray-300">
            <div className="bg-gray-200 border border-gray-300"></div>
            {weekDays.map((day) => (
              <div
                key={day.date}
                className={`bg-gray-200 border border-gray-300 text-center font-semibold p-2 ${
                  isHoliday(day.fullDate) ? "bg-red-100" : ""
                }`}
              >
                {`${day.day} ${day.date}`}
              </div>
            ))}
            {timeSlots.map((slot, slotIndex) => (
              <React.Fragment key={slot}>
                <div className="border border-gray-300 bg-gray-100 text-center p-1 text-sm font-mono sticky left-0 z-10">
                  {slot}
                </div>
                {weekDays.map((day, dayIndex) => {
                  const isHolidayDay = isHoliday(day.fullDate);
                  const lessons = videoLessons.filter(
                    (lesson) =>
                      lesson.date === format(day.fullDate, "yyyy-MM-dd") &&
                      getVideoLessonSlots(lesson, slot)
                  );

                  if (isHolidayDay) {
                    return (
                      <div
                        key={`${day.date}-${slot}`}
                        className="border border-gray-300 bg-gray-200 cursor-not-allowed"
                      ></div>
                    );
                  }
                  if (lessons.length > 0) {
                    // Find the earliest lesson to determine start time and duration
                    const earliestLesson = lessons.reduce((earliest, lesson) => {
                      const lessonStart = parse(lesson.start_time, "HH:mm:ss", new Date());
                      const earliestStart = parse(earliest.start_time, "HH:mm:ss", new Date());
                      return lessonStart < earliestStart ? lesson : earliest;
                    }, lessons[0]);

                    const lessonStart = parse(earliestLesson.start_time, "HH:mm:ss", new Date());
                    const lessonEnd = parse(earliestLesson.end_time, "HH:mm:ss", new Date());
                    const durationMinutes = differenceInMinutes(lessonEnd, lessonStart);
                    const durationSlots = Math.max(1, Math.ceil(durationMinutes / 30));

                    const [slotStartStr] = slot.split(" - ");
                    const slotStart = parse(slotStartStr, "HH:mm", new Date());
                    const isFirstSlot =
                      lessonStart.getHours() === slotStart.getHours() &&
                      lessonStart.getMinutes() === slotStart.getMinutes();

                    if (isFirstSlot) {
                      // Look up the defined slot for color + occupancy
                      const dayDateStr = format(day.fullDate, "yyyy-MM-dd");

                      const matchingSlot = definedSlots.find((s) => {
                        if (s.date !== dayDateStr) return false;

                        const definedStart = parse(s.start_time, "HH:mm:ss", new Date());
                        return (
                          definedStart.getHours() === lessonStart.getHours() &&
                          definedStart.getMinutes() === lessonStart.getMinutes()
                        );
                      });

                      const colorClass = matchingSlot
                        ? getSlotColorClass(matchingSlot.color)
                        : "bg-blue-200 border-blue-400";

                      const occupancyText = matchingSlot?.occupancy_ratio ?? lessons.length;

                      return (
                        <div
                          key={`${day.date}-${slot}`}
                          className={`${colorClass} border rounded cursor-pointer relative`}
                          style={{ gridRowEnd: `span ${durationSlots}` }}
                          onClick={() => handleVideoCellClick(day.day, slot, lessons)}
                        >
                          <div className="absolute top-1 left-0 right-0 flex justify-center text-xs font-bold text-gray-800">
                            {occupancyText}
                          </div>
                        </div>
                      );
                    }

                    return null;
                  }
                  return (
                    <div
                      key={`${day.date}-${slot}`}
                      className="border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleVideoCellClick(day.day, slot, [])}
                    ></div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="w-80 shrink-0 bg-gray-50 p-4 border-l border-gray-200">
          <div className="flex border-b border-gray-200 mb-4">
            <button
              className={`px-4 py-2 font-semibold flex-1 ${sidebarTab === "daily" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setSidebarTab("daily")}
            >
              Daily Schedule
            </button>
            <button
              className={`px-4 py-2 font-semibold flex-1 ${sidebarTab === "active" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setSidebarTab("active")}
            >
              Active Schedule
            </button>
          </div>
          {sidebarTab === "daily" ? (
            <>
              <h3 className="font-bold mb-4 text-lg">Today's Schedule</h3>
              {todaysVideoLessons.length === 0 ? (
                <p className="text-gray-500 italic">No lessons scheduled for today.</p>
              ) : (
                <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {todaysVideoLessons.map((lesson) => {
                    const studentName = `${lesson.surname || ""} ${lesson.first_name || ""}`.trim() || "Unknown Student";
                    return (
                      <div key={lesson.lesson_id} className="p-2 bg-white rounded border shadow-sm text-sm">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs">Time: {lesson.start_time?.slice(0, 5) || 'N/A'}</span>
                          <span className="text-xs">ID: {lesson.lesson_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs font-medium">Student: {studentName}</span>
                          <span className="text-xs">Stu ID: {lesson.student_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs">Attendance Code:  {lesson.attendance_code}</span>
                        </div>
                      </div>  
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <h3 className="font-bold mb-4 text-lg">Active Schedules</h3>
                <button
                  onClick={() => setIsAddIpadModalOpen(true)}
                  className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1"
                >
                  <HiPlusCircle className="w-4 h-4" />
                  Add iPad Capacity
                </button>
              {timeSlotRecords.length === 0 ? (
                <p className="text-gray-500 italic">No active time slot records found.</p>
              ) : (
                <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {timeSlotRecords.map((record) => {
                    const startDate = new Date(record.start_date);
                    const endDate = new Date(record.end_date);
                    const ctimeDate = new Date(record.ctime);
                    const startFormatted = isValid(startDate) ? format(startDate, "MMM d, yyyy") : 'Invalid Date';
                    const endFormatted = isValid(endDate) ? format(endDate, "MMM d, yyyy") : 'Invalid Date';
                    const ctimeFormatted = isValid(ctimeDate) ? format(ctimeDate, "MMM d, yyyy HH:mm") : 'Invalid Date';
                    return (
                      <div key={record.id} className="p-3 bg-white rounded border shadow-sm">
                        <p className="font-semibold mb-2">
                          Effective Period: {startFormatted} - {endFormatted}
                        </p>
                        <div className="space-y-1 text-sm">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                            const startTime = record[`${day.toLowerCase()}_start_time`]?.slice(0, 5);
                            const endTime = record[`${day.toLowerCase()}_end_time`]?.slice(0, 5);
                            return (
                              <div key={day} className="flex justify-between">
                                <span>{day}:</span>
                                <span>{startTime ? `${startTime} - ${endTime}` : 'N/A'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {isVideoLessonModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-3/4 max-w-md max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Lesson Details</h2>
            {selectedVideoLessons.length === 0 ? (
              <p>No lessons scheduled in this time slot.</p>
            ) : (
              selectedVideoLessons.map((lesson) => {
                const lessonDateParsed = parse(lesson.date || '', "yyyy-MM-dd", new Date());
                const lessonDateFormatted = isValid(lessonDateParsed)
                  ? format(lessonDateParsed, "MMM d, yyyy")
                  : 'Invalid Date';

                return (
                  <div key={lesson.lesson_id} className="mb-3 border-b pb-2 last:border-b-0">
                    <p className="font-semibold">
                      {`${lesson.surname || ""} ${lesson.first_name || ""}`.trim() || "Unknown Student"}
                    </p>
                    <p>Student ID: {lesson.student_id}</p>
                    <p>Type: {lesson.type === "video" ? "Video Lesson" : "Makeup Lesson"}</p>

                    {/* Added: Current Video Course */}
                    <p>
                      Current Video Course:{" "}
                      {lesson.current_video_course
                        ? lesson.current_video_course
                        : "—"}
                    </p>

                    <p>Lesson ID: {lesson.lesson_id}</p>

                    {lesson.lesson_id_label && (
                      <p>Lesson Label: {lesson.lesson_id_label}</p>
                    )}

                    <p>Date: {lessonDateFormatted}</p>
                    <p>Time: {lesson.start_time?.slice(0, 5) || 'N/A'} - {lesson.end_time?.slice(0, 5) || 'N/A'}</p>
                  </div>
                );
              })
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setIsVideoLessonModalOpen(false);
                  setSelectedVideoLessons([]);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add iPad Capacity Modal */}
      {isAddIpadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add iPad Capacity</h2>
              <button
                onClick={() => {
                  setIsAddIpadModalOpen(false);
                  setIpadFormData({ startDate: "", endDate: "", capacity: 1 });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={ipadFormData.startDate}
                  onChange={(e) =>
                    setIpadFormData({ ...ipadFormData, startDate: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date <span className="text-gray-400 text-xs">(leave empty for single day)</span>
                </label>
                <input
                  type="date"
                  value={ipadFormData.endDate}
                  min={ipadFormData.startDate}
                  onChange={(e) =>
                    setIpadFormData({ ...ipadFormData, endDate: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* iPad Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  iPad Amount
                </label>
                <input
                  type="number"
                  min="1"
                  value={ipadFormData.capacity}
                  onChange={(e) =>
                    setIpadFormData({
                      ...ipadFormData,
                      capacity: Math.max(1, Number(e.target.value)),
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsAddIpadModalOpen(false);
                  setIpadFormData({ startDate: "", endDate: "", capacity: 1 });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIpadCapacity}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ===== Urgent Time Slot Modal ===== */}
      {isUrgentTimeSlotModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Urgent Time Slot</h2>
              <button
                onClick={() => {
                  setIsUrgentTimeSlotModalOpen(false);
                  setUrgentFormData({ date: "", start_time: "", end_time: "", ipad_capacity: 1 });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={urgentFormData.date}
                  onChange={(e) =>
                    setUrgentFormData({ ...urgentFormData, date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Start Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time (24h)
                </label>
                <input
                  type="text"
                  placeholder="14:30"
                  value={urgentFormData.start_time}
                  onChange={(e) =>
                    setUrgentFormData({ ...urgentFormData, start_time: e.target.value })
                  }
                  pattern="^([01]?[0-9]|2[0-3]):[0-5][0-9]$"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* End Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time (24h)
                </label>
                <input
                  type="text"
                  placeholder="16:00"
                  value={urgentFormData.end_time}
                  onChange={(e) =>
                    setUrgentFormData({ ...urgentFormData, end_time: e.target.value })
                  }
                  pattern="^([01]?[0-9]|2[0-3]):[0-5][0-9]$"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* iPad Capacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  iPad Capacity
                </label>
                <input
                  type="number"
                  min="1"
                  value={urgentFormData.ipad_capacity}
                  onChange={(e) =>
                    setUrgentFormData({
                      ...urgentFormData,
                      ipad_capacity: Math.max(1, Number(e.target.value)),
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsUrgentTimeSlotModalOpen(false);
                  setUrgentFormData({ date: "", start_time: "", end_time: "", ipad_capacity: 1 });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUrgentTimeSlot}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div className="p-8 h-screen overflow-y-auto">
      <h1 className="text-2xl font-bold mb-4">Timetable</h1>
      <div className="mb-4 border-t border-gray-400 w-full"></div>
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 font-semibold ${activeTab === "live" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("live")}
        >
          Live Lesson
        </button>
        <button
          className={`px-4 py-2 font-semibold ${activeTab === "video" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}
          onClick={() => setActiveTab("video")}
        >
          Video Lesson
        </button>
      </div>
      {activeTab === "live" && renderLiveCalendar()}
      {activeTab === "video" && renderVideoCalendar()}

      {isQRModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96 text-center">
            <h2 className="text-xl font-bold mb-4">QR Code for Attendance</h2>
            <div className="flex justify-center mb-4 p-4 bg-gray-50 rounded">
              <QRCodeSVG value={selectedQRCode} size={256} />
            </div>
            {/* <p className="text-sm text-gray-600 mb-4">Scan this QR code with a mobile device to retrieve the attendance code: <strong>{selectedQRCode}</strong></p> */}
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsQRModalOpen(false);
                  setSelectedQRCode('');
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isCurrentQRModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96 text-center">
            <h2 className="text-xl font-bold mb-4">Current/Next Class QR Code</h2>
            {currentLessonInfo ? (
              <>
                <p className="mb-2">
                  Class: {currentLessonInfo.class_name}<br />
                  Date: {format(parse(currentLessonInfo.lesson_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}<br />
                  Time: {currentLessonInfo.start_time.slice(0, 5)} - {currentLessonInfo.end_time.slice(0, 5)}
                </p>
                <div className="flex justify-center mb-4 p-4 bg-gray-50 rounded">
                  <QRCodeSVG value={currentLessonInfo.attendance_code} size={256} />
                </div>
              </>
            ) : (
              <p className="text-red-500 mb-4">No current or upcoming lesson found.</p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsCurrentQRModalOpen(false);
                  setCurrentLessonInfo(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isScheduleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-3/4 max-w-3xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add Schedule</h2>
            <div>
              <label className="block font-medium">Start Date</label>
              <input
                type="date"
                className="border p-2 w-full mb-4"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block font-medium">End Date</label>
              <input
                type="date"
                className="border p-2 w-full mb-4"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block font-medium">iPad Capacity</label>
              <input
                type="number"
                className="border p-2 w-full mb-4"
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
              />
            </div>
            {Object.keys(formData.timeSlots).map((day) => (
              <div key={day} className="mt-4">
                <div className="font-medium mb-2">{day}</div>
                <div className="flex space-x-4">
                  <div className="flex-grow">
                    <label className="block">Opening Time (24h)</label>
                    <input
                      type="text"
                      placeholder="09:00"
                      value={formData.timeSlots[day].opening}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          timeSlots: {
                            ...formData.timeSlots,
                            [day]: { ...formData.timeSlots[day], opening: e.target.value },
                          },
                        })
                      }
                      pattern="^([01]?[0-9]|2[0-3]):[0-5][0-9]$"
                      className="border p-2 w-full"
                    />
                  </div>
                  <div className="flex-grow">
                    <label className="block">Closing Time (24h)</label>
                    <input
                      type="text"
                      placeholder="18:00"
                      value={formData.timeSlots[day].closing}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          timeSlots: {
                            ...formData.timeSlots,
                            [day]: { ...formData.timeSlots[day], closing: e.target.value },
                          },
                        })
                      }
                      pattern="^([01]?[0-9]|2[0-3]):[0-5][0-9]$"
                      className="border p-2 w-full"
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-6 flex justify-end space-x-2">
              <button
                onClick={() => setIsScheduleModalOpen(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleFormSubmit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {isHoldTimeSlotModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-3/4 max-w-md max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Hold Time Slot</h2>
            <div className="mb-4">
              <label className="block font-medium mb-1">Select Date</label>
              <select
                className="border p-2 w-full"
                value={holdFormData.selectedDate}
                onChange={(e) => setHoldFormData({ selectedDate: e.target.value, selectedTimeSlot: "", remark: "" })}
              >
                <option value="">Select a date</option>
                {weekDays.map((day) => {
                  const validDate = day.fullDate instanceof Date && !isNaN(day.fullDate);
                  return (
                    <option key={day.date} value={validDate ? day.fullDate.toISOString() : ''}>
                      {validDate ? format(addHours(day.fullDate, 8), 'MMM d, yyyy') : 'Invalid Date'}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="mb-4">
              <label className="block font-medium mb-1">Select Time Slot</label>
              <select
                className="border p-2 w-full"
                value={holdFormData.selectedTimeSlot}
                onChange={(e) => setHoldFormData({ ...holdFormData, selectedTimeSlot: e.target.value })}
                disabled={isFetchingTimeSlots || !holdFormData.selectedDate || availableTimeSlots.length === 0}
              >
                <option value="">Select a time slot</option>
                {isFetchingTimeSlots ? (
                  <option value="" disabled>Loading time slots...</option>
                ) : availableTimeSlots.length === 0 && holdFormData.selectedDate ? (
                  <option value="" disabled>No available time slots</option>
                ) : (
                  availableTimeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))
                )}
              </select>
              {fetchError && <p className="text-red-500 text-sm mt-2">{fetchError}</p>}
            </div>
            <div className="mb-4">
              <label className="block font-medium mb-1">Remark</label>
              <input
                type="text"
                className="border p-2 w-full"
                value={holdFormData.remark}
                onChange={(e) => setHoldFormData({ ...holdFormData, remark: e.target.value })}
                placeholder="Enter a remark (optional)"
                maxLength={255}
              />
            </div>
            <div className="mb-4">
              <h3 className="font-medium mb-2">Held Time Slots</h3>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">Date</th>
                      <th className="border p-2 text-left">Time Slot</th>
                      <th className="border p-2 text-left">Remark</th>
                      <th className="border p-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heldTimeSlots.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="border p-2 text-center text-gray-500">
                          No time slots held
                        </td>
                      </tr>
                    ) : (
                      heldTimeSlots
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map((slot) => (
                          <tr key={slot.booking_id}>
                            <td className="border p-2">
                              {(() => {
                                if (!slot.date) return '-';
                                let parsed = new Date(slot.date);
                                if (isNaN(parsed)) {
                                  parsed = parse(slot.date, 'yyyy-MM-dd', new Date());
                                }
                                return isNaN(parsed) ? '-' : format(parsed, 'MMM d, yyyy');
                              })()}
                            </td>
                            <td className="border p-2">{slot.timeSlot}</td>
                            <td className="border p-2">{slot.remark || '-'}</td>
                            <td className="border p-2">
                              <button
                                className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${process.env.REACT_APP_API_URL}/admin/deleteHeldTimeSlot/${slot.booking_id}`);
                                    setHeldTimeSlots(heldTimeSlots.filter((s) => s.booking_id !== slot.booking_id));
                                  } catch (error) {
                                    console.error('Error deleting held time slot:', error);
                                    alert('Failed to delete held time slot.');
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
              {heldTimeSlots.length > itemsPerPage && (
                <div className="flex justify-between mt-4">
                  <button
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded disabled:opacity-50"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="self-center">
                    Page {currentPage} of {Math.ceil(heldTimeSlots.length / itemsPerPage)}
                  </span>
                  <button
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded disabled:opacity-50"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(heldTimeSlots.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(heldTimeSlots.length / itemsPerPage)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setIsHoldTimeSlotModalOpen(false);
                  setHoldFormData({ selectedDate: "", selectedTimeSlot: "", remark: "" });
                  setAvailableTimeSlots([]);
                  setFetchError(null);
                  setCurrentPage(1);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (holdFormData.selectedDate && holdFormData.selectedTimeSlot) {
                    try {
                      await axios.post(`${process.env.REACT_APP_API_URL}/admin/holdTimeSlot`, {
                        selectedDate: holdFormData.selectedDate,
                        timeSlot: holdFormData.selectedTimeSlot,
                        remark: holdFormData.remark,
                      });
                      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getHeldTimeSlots`);
                      setHeldTimeSlots(response.data);
                      setHoldFormData({ selectedDate: "", selectedTimeSlot: "", remark: "" });
                      setAvailableTimeSlots([]);
                      alert('Time slot held successfully.');
                      setCurrentPage(1);
                      setIsHoldTimeSlotModalOpen(false);
                    } catch (error) {
                      console.error('Error holding time slot:', error);
                      alert(error.response?.data?.message || 'Failed to hold time slot.');
                    }
                  } else {
                    alert('Please select both a date and a time slot.');
                  }
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}